import TimeoutPromise from 'app/utils/TimeoutPromise';
import ParallelTaskScheduler from 'app/utils/async/ParallelTaskScheduler';
import { TaskScheduler } from 'app/utils/async/TaskScheduler';
import type ModbusRTU from 'modbus-serial';
import type {
  ReadCoilResult,
  ReadExceptionStatus,
  ReadRegisterResult,
  WriteCoilResult,
  WriteMaskRegisterResult,
  WriteMultipleResult,
  WriteRegisterResult,
} from 'modbus-serial/ModbusRTU';

export default class SequentialModbusRTU {
  public modbusClient: ModbusRTU;
  public promiseScheduler: TaskScheduler;

  constructor(modbusClient: ModbusRTU) {
    this.modbusClient = modbusClient;
    this.promiseScheduler = new ParallelTaskScheduler(1);
  }

  scheduleFunction<T>(
    priority: number,
    func: (...args: any[]) => Promise<T>,
    ...params: any[]
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      this.promiseScheduler.schedule(() => {
        let attempts = 0;
        const maxAttempts = 3;
        const funcExec = (): Promise<void> =>
          func(...params)
            .then(resolve)
            .catch(async (err) => {
              if (
                err.message.includes('retry request again later') ||
                err.message.includes('CRC error') ||
                err.message.includes('Timeout')
              ) {
                console.debug(`ModbusRTU: Retrying request due to error: `, err);
                if (attempts < maxAttempts) {
                  attempts++;
                  await TimeoutPromise(50);
                  return funcExec();
                }
              }
              return reject(err);
            });

        return funcExec();
      });
    });
  }

  open(callback?: Function): void {
    this.modbusClient.open(callback);
  }
  close(callback?: Function): void {
    this.modbusClient.close(callback);
  }
  destroy(callback?: Function): void {
    this.modbusClient.destroy(callback);
  }

  // Promise API
  setID(id: number): void {
    this.modbusClient.setID(id);
  }
  getID(): number {
    return this.modbusClient.getID();
  }
  setTimeout(duration: number): void {
    this.modbusClient.setTimeout(duration);
  }
  getTimeout(): number {
    return this.modbusClient.getTimeout();
  }

  readCoils(dataAddress: number, length: number): Promise<ReadCoilResult> {
    return this.scheduleFunction(
      0,
      this.modbusClient.readCoils.bind(this.modbusClient),
      dataAddress,
      length
    );
  }
  readDiscreteInputs(dataAddress: number, length: number): Promise<ReadCoilResult> {
    return this.scheduleFunction(
      0,
      this.modbusClient.readDiscreteInputs.bind(this.modbusClient),
      dataAddress,
      length
    );
  }
  readHoldingRegisters(dataAddress: number, length: number): Promise<ReadRegisterResult> {
    return this.scheduleFunction(
      0,
      this.modbusClient.readHoldingRegisters.bind(this.modbusClient),
      dataAddress,
      length
    );
  }
  readRegistersEnron(dataAddress: number, length: number): Promise<ReadRegisterResult> {
    return this.scheduleFunction(
      0,
      this.modbusClient.readRegistersEnron.bind(this.modbusClient),
      dataAddress,
      length
    );
  }
  readInputRegisters(dataAddress: number, length: number): Promise<ReadRegisterResult> {
    return this.scheduleFunction(
      0,
      this.modbusClient.readInputRegisters.bind(this.modbusClient),
      dataAddress,
      length
    );
  }
  writeCoil(dataAddress: number, state: boolean): Promise<WriteCoilResult> {
    return this.scheduleFunction(
      0,
      this.modbusClient.writeCoil.bind(this.modbusClient),
      dataAddress,
      state
    );
  }
  writeCoils(dataAddress: number, states: Array<boolean>): Promise<WriteMultipleResult> {
    return this.scheduleFunction(
      0,
      this.modbusClient.writeCoils.bind(this.modbusClient),
      dataAddress,
      states
    );
  }
  writeRegister(dataAddress: number, value: number): Promise<WriteRegisterResult> {
    return this.scheduleFunction(
      0,
      this.modbusClient.writeRegister.bind(this.modbusClient),
      dataAddress,
      value
    );
  }
  writeRegisterEnron(dataAddress: number, value: number): Promise<WriteRegisterResult> {
    return this.scheduleFunction(
      0,
      this.modbusClient.writeRegisterEnron.bind(this.modbusClient),
      dataAddress,
      value
    );
  }
  readExceptionStatus(): Promise<ReadExceptionStatus> {
    return this.scheduleFunction(0, this.modbusClient.readExceptionStatus.bind(this.modbusClient));
  }
  writeRegisters(
    dataAddress: number,
    values: Array<number> | Buffer
  ): Promise<WriteMultipleResult> {
    return this.scheduleFunction(
      0,
      this.modbusClient.writeRegisters.bind(this.modbusClient),
      dataAddress,
      values
    );
  }
  maskWriteRegister(
    dataAddress: number,
    andMask: number,
    orMask: number
  ): Promise<WriteMaskRegisterResult> {
    return this.scheduleFunction(
      0,
      this.modbusClient.maskWriteRegister.bind(this.modbusClient),
      dataAddress,
      andMask,
      orMask
    );
  }

  on(event: 'close', listener: () => unknown): this;
  on(event: 'error', listener: (error: unknown) => unknown): this;
  on(event: 'close' | 'error', listener: (error?: unknown) => unknown): this {
    this.modbusClient.on(event, listener);
    return this;
  }
  isOpen: boolean;
}
